import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function NewProject() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [teamLeads, setTeamLeads] = useState([]);
  const [form, setForm] = useState({ name: "", site_url: "", team_lead_id: "", content_manager_id: "" });
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/users");
      setTeamLeads(data.filter((u) => u.role === "team_lead"));
      setManagers(data.filter((u) => u.role === "content_manager"));
    })();
  }, []);

  // Roles allowed by the backend POST /projects (mirror here so the page mounts
  // for the same set; button on ProjectsList uses the identical whitelist).
  const canCreate = user && ["admin", "super_admin", "department_admin", "content_manager", "team_lead"].includes(user.role);
  if (!canCreate) {
    return <div className="p-8 text-slate-500">You don't have permission to create projects.</div>;
  }

  const submit = async () => {
    if (!form.name || !form.team_lead_id) {
      toast.error("Name and team lead are required");
      return;
    }
    try {
      const payload = { name: form.name, site_url: form.site_url, team_lead_ids: [form.team_lead_id] };
      if (form.content_manager_id) payload.content_manager_ids = [form.content_manager_id];
      const { data } = await api.post("/projects", payload);
      toast.success("Project created");
      nav(`/projects/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin" data-testid="new-project-page">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-8 py-4">
        <h1 className="font-display text-2xl font-bold text-slate-900">New project</h1>
        <p className="text-sm text-slate-500 mt-0.5">Create a site under a Team Lead</p>
      </div>

      <div className="p-8 max-w-2xl">
        <Card className="p-6 space-y-4 bg-white border-slate-200">
          <div>
            <Label>Project (Site) name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. TechRadar Blog" data-testid="new-project-name" />
          </div>
          <div>
            <Label>Site URL</Label>
            <Input value={form.site_url} onChange={(e) => setForm({ ...form, site_url: e.target.value })} placeholder="https://…" data-testid="new-project-url" />
          </div>
          <div>
            <Label>Team Lead</Label>
            <Select value={form.team_lead_id} onValueChange={(v) => setForm({ ...form, team_lead_id: v })}>
              <SelectTrigger data-testid="new-project-tl"><SelectValue placeholder="Select team lead" /></SelectTrigger>
              <SelectContent>
                {teamLeads.map((tl) => <SelectItem key={tl.id} value={tl.id}>{tl.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(user?.role === "admin" || user?.role === "super_admin") && (
            <div>
              <Label>Content Manager (optional)</Label>
              <Select value={form.content_manager_id || "none"} onValueChange={(v) => setForm({ ...form, content_manager_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Auto from team lead" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Auto-derive</SelectItem>
                  {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="pt-2 flex justify-end gap-2">
            <Button variant="outline" onClick={() => nav(-1)}>Cancel</Button>
            <Button onClick={submit} className="bg-purple-600 hover:bg-purple-700" data-testid="new-project-submit">Create project</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
